/**
 * Lesson Store (Zustand)
 * Manages lesson path browsing, active lesson state, exercise progress,
 * completion submission, and the spaced-repetition due queue.
 */

import { create } from 'zustand';
import { api } from '../services/api';
import { HTTP_AI_TIMEOUT_MS } from '../config/timeouts';
import { errorMessage } from '../generated/api/endpoint-errors';
import type { ScheduledLessonsResponse } from '../generated/api/http-contracts';
import { createLogger } from '../utils/logger';
import { useUserPreferencesStore } from './userPreferencesStore';
import type {
  LessonPathData,
  CEFRLevel,
  LessonDetail,
  ExerciseProgress,
  ExerciseResult,
  GeneratedExercise,
  AiAgentInstructions,
  CompletionResult,
  RecommendedLesson,
} from '../types/lesson';

const log = createLogger('LessonStore');

/**
 * How many error-driven recommendations the Practice card asks for. Kept small
 * on purpose: the card sits above the lesson path and is a nudge, not a list.
 */
export const RECOMMENDED_LESSONS_LIMIT = 3;

/** A level the server serves nothing for — renders as the empty state. */
const EMPTY_LESSON_PATH: LessonPathData = { sections: [], continueLesson: null };

/** One row of the due-for-review queue (GET /api/lessons/scheduled). */
export type ScheduledLessonItem = ScheduledLessonsResponse['lessons'][number];

interface LessonState {
  // Flat path state
  lessonPath: LessonPathData | null;
  selectedLevel: CEFRLevel;
  isLoadingPath: boolean;
  pathError: string | null;

  // Error-pattern recommendations (Practice card)
  recommendations: RecommendedLesson[];
  isLoadingRecommendations: boolean;
  recommendationsError: string | null;

  // Active lesson state
  currentLesson: LessonDetail | null;
  isLoadingLesson: boolean;
  exerciseProgress: Record<string, ExerciseProgress>;
  aiGeneratedExercises: GeneratedExercise[] | null;
  isGeneratingExercises: boolean;

  // Completion
  isSubmittingCompletion: boolean;
  lastCompletionResult: CompletionResult | null;

  // Due for review (LESSON-006) — server-ordered, most overdue first
  dueLessons: ScheduledLessonItem[];
  /** Eligible due lessons before `limit`; `dueLessons.length` caps at the page. */
  dueTotal: number;
  dueLoaded: boolean;
  dueLoading: boolean;
  dueError: string | null;

  // Per-operation errors
  lessonError: string | null;
  exerciseError: string | null;
  exerciseErrorCode: string | null;
  completionError: string | null;

  // Lesson update flags (FRESH-005)
  lessonUpdatesAvailable: string[];  // course IDs with available updates

  // Actions
  fetchLessonPath: (level: CEFRLevel) => Promise<void>;
  fetchRecommendations: () => Promise<void>;
  setSelectedLevel: (level: CEFRLevel) => void;
  fetchLesson: (slug: string) => Promise<void>;
  generateExercises: (slug: string, instructions: AiAgentInstructions) => Promise<void>;
  clearExerciseError: () => void;
  updateExerciseProgress: (exerciseId: string, progress: ExerciseProgress) => void;
  submitCompletion: (slug: string) => Promise<CompletionResult>;
  dismissCompletion: () => void;
  retryLesson: () => void;
  loadDueLessons: () => Promise<void>;
  resetLessonState: () => void;
  clearErrors: () => void;
  setLessonUpdatesAvailable: (courseIds: string[]) => void;
  clearLessonUpdate: (courseId: string) => void;
}

export const useLessonStore = create<LessonState>((set, get) => ({
  // Initial state
  lessonPath: null,
  selectedLevel: 'A1',
  isLoadingPath: false,
  pathError: null,
  recommendations: [],
  isLoadingRecommendations: false,
  recommendationsError: null,
  currentLesson: null,
  isLoadingLesson: false,
  exerciseProgress: {},
  aiGeneratedExercises: null,
  isGeneratingExercises: false,
  isSubmittingCompletion: false,
  lastCompletionResult: null,
  dueLessons: [],
  dueTotal: 0,
  dueLoaded: false,
  dueLoading: false,
  dueError: null,
  lessonError: null,
  exerciseError: null,
  exerciseErrorCode: null,
  completionError: null,
  lessonUpdatesAvailable: [],

  /**
   * Fetch flat lesson path for a CEFR level.
   *
   * ADR-033 M6: during a staged rollout this client knows levels an N-1 server
   * does not (C2), and the server answers `level=C2` with 400 VALIDATION_FAILED.
   * That means "this server has no such level" — nothing the learner can retry
   * away — so it renders as the level's empty state, exactly like a level the
   * server knows but has no lessons for. Self-healing: once the backend catches
   * up, the same request returns real content.
   */
  fetchLessonPath: async (level: CEFRLevel) => {
    set({ isLoadingPath: true, pathError: null });
    const result = await api.getLessonPath({ params: { level } });
    if (result.ok) {
      set({ lessonPath: result.data, isLoadingPath: false });
    } else if (result.kind === 'domain' && result.error.code === 'VALIDATION_FAILED') {
      log.warn('Server rejected CEFR level — treating as empty level', { level });
      set({ lessonPath: EMPTY_LESSON_PATH, isLoadingPath: false });
    } else {
      set({ isLoadingPath: false, pathError: errorMessage(result) });
    }
  },

  /**
   * Fetch lessons recommended from the learner's accumulated error patterns.
   *
   * Secondary content: on failure we keep whatever loaded last and leave the
   * error in state for diagnostics only — the Practice card hides itself
   * rather than growing an error surface above the lesson path.
   */
  fetchRecommendations: async () => {
    set({ isLoadingRecommendations: true, recommendationsError: null });
    const result = await api.getRecommendedLessons({
      params: { limit: RECOMMENDED_LESSONS_LIMIT },
    });
    if (result.ok) {
      set({ recommendations: result.data.lessons, isLoadingRecommendations: false });
    } else {
      set({ isLoadingRecommendations: false, recommendationsError: errorMessage(result) });
    }
  },

  /**
   * Set selected level and trigger path fetch
   */
  setSelectedLevel: (level: CEFRLevel) => {
    set({ selectedLevel: level });
    get().fetchLessonPath(level);
  },

  /**
   * Fetch a specific lesson by slug
   */
  fetchLesson: async (slug: string) => {
    set({ isLoadingLesson: true, lessonError: null, currentLesson: null, exerciseProgress: {}, aiGeneratedExercises: null });
    const result = await api.getLesson(slug);
    if (result.ok) {
      set({ currentLesson: result.data, isLoadingLesson: false });
    } else {
      set({ isLoadingLesson: false, lessonError: errorMessage(result) });
    }
  },

  /**
   * Generate AI exercises for a lesson section
   */
  generateExercises: async (slug: string, instructions: AiAgentInstructions) => {
    set({ isGeneratingExercises: true, exerciseError: null, exerciseErrorCode: null });
    const courseId = useUserPreferencesStore.getState().activeCourseId ?? '';
    const result = await api.generateExercises(slug, {
      courseId,
      aiAgentInstructions: {
        ...instructions,
        personalize: instructions.personalize ?? false,
        freshOnEveryVisit: instructions.freshOnEveryVisit ?? false,
      },
    }, { timeout: HTTP_AI_TIMEOUT_MS }); // LLM generation — long-op budget, not fail-fast default
    if (result.ok) {
      set({ aiGeneratedExercises: result.data.exercises as unknown as GeneratedExercise[], isGeneratingExercises: false });
    } else {
      const code = result.error.code;
      set({ isGeneratingExercises: false, exerciseError: errorMessage(result), exerciseErrorCode: code });
    }
  },

  clearExerciseError: () => {
    set({ exerciseError: null, exerciseErrorCode: null });
  },

  /**
   * Update progress for a specific exercise
   */
  updateExerciseProgress: (exerciseId: string, progress: ExerciseProgress) => {
    set((state) => ({
      exerciseProgress: {
        ...state.exerciseProgress,
        [exerciseId]: progress,
      },
    }));
  },

  /**
   * Submit lesson completion with scores
   */
  submitCompletion: async (slug: string) => {
    const { exerciseProgress } = get();

    // Calculate overall score from exercise progress
    const exercises = Object.values(exerciseProgress);
    const totalScore = exercises.reduce((sum, ex) => sum + ex.score, 0);
    const score = exercises.length > 0 ? Math.round(totalScore / exercises.length) : 0;

    // Build exercise results map
    const exerciseResults: Record<string, ExerciseResult> = {};
    for (const [id, progress] of Object.entries(exerciseProgress)) {
      exerciseResults[id] = {
        correct: progress.correct,
        score: progress.score,
        type: progress.type,
        voiceUsed: progress.voiceUsed,
      };
    }

    set({ isSubmittingCompletion: true, completionError: null });
    const result = await api.completeLessonAttempt(slug, { score, exerciseResults });
    if (result.ok) {
      set({ lastCompletionResult: result.data, isSubmittingCompletion: false });
      return result.data;
    }
    set({ isSubmittingCompletion: false, completionError: errorMessage(result) });
    throw new Error(errorMessage(result));
  },

  /**
   * Load the spaced-repetition due queue (DUE-001..003).
   *
   * Keeps the previously-loaded list on failure so the Practice hub card
   * degrades from counts to a neutral CTA only when nothing has ever
   * loaded — the hub must never grow an error surface for a secondary
   * card. The Due screen reads `dueError` and does report it.
   */
  loadDueLessons: async () => {
    set({ dueLoading: true, dueError: null });
    const result = await api.getScheduledLessons();
    if (result.ok) {
      set({
        dueLessons: result.data.lessons,
        dueTotal: result.data.total,
        dueLoaded: true,
        dueLoading: false,
      });
    } else {
      set({ dueLoading: false, dueError: errorMessage(result) });
    }
  },

  /**
   * Dismiss the score card and leave the lesson (LSC-002). The screen
   * defers its goBack() until this clears, so the learner sees the
   * schedule instead of being bounced straight out.
   */
  dismissCompletion: () => {
    set({ lastCompletionResult: null, completionError: null });
  },

  /**
   * Dismiss the score card and re-attempt in place (LSC-003). Exercise
   * progress is cleared so the retry scores from scratch; the lesson
   * itself stays loaded.
   */
  retryLesson: () => {
    set({ lastCompletionResult: null, completionError: null, exerciseProgress: {} });
  },

  /**
   * Reset active lesson state (when leaving lesson)
   */
  resetLessonState: () => {
    set({
      currentLesson: null,
      exerciseProgress: {},
      aiGeneratedExercises: null,
      lastCompletionResult: null,
      lessonError: null,
      exerciseError: null,
      exerciseErrorCode: null,
      completionError: null,
    });
  },

  /**
   * Clear all errors
   */
  clearErrors: () => {
    set({
      pathError: null,
      recommendationsError: null,
      lessonError: null,
      exerciseError: null,
      exerciseErrorCode: null,
      completionError: null,
    });
  },

  /**
   * Set lesson update flags for courses that have newer lessons (FRESH-005).
   * Merges with existing flags — doesn't overwrite.
   */
  setLessonUpdatesAvailable: (courseIds: string[]) => {
    const current = new Set(get().lessonUpdatesAvailable);
    for (const id of courseIds) current.add(id);
    set({ lessonUpdatesAvailable: [...current] });
  },

  /**
   * Clear lesson update flag for a course (after user taps "Update lessons").
   */
  clearLessonUpdate: (courseId: string) => {
    set({ lessonUpdatesAvailable: get().lessonUpdatesAvailable.filter((id) => id !== courseId) });
  },
}));
